/* ---------------------------------------------------------------------------
   Appendix D, and the two things it lets a measurement say.

   `QC-04` (a measured width suggests a condition rating) and the arithmetic half
   of `QC-01`/`QC-03` (a measured proportion suggests an extent band).

   THE SOURCE. *Guidelines for Inspection and Maintenance of Bridges, Volume 1,
   Part 2*, §5.3 for the three scales and Appendix D — "Guide to condition
   rating" — for the per-element tables. Appendix D is mostly qualitative:
   "minor crazing, cracking, fretting", "average weathering". It is numeric in
   exactly one place, and that place is the one an inspector can carry an
   instrument for.

   THE BANDS WERE READ OFF THE PAGE BY COLUMN, NOT FROM MEMORY. Appendix D is a
   five-column table — Good 0 · Fair 1 · Below Average 2 · Poor 3 · Serious 4 —
   printed in a layout where a phrase belongs to whichever column it starts
   under. The first draft of this feature had the crack bands one rating too
   severe, from reading the phrases in reading order rather than by column. The
   values below are the columns:

     Good 0          "Cracks in concrete < 0.3 mm"
     Fair 1          "Concrete cracks > 0.3mm <1mm"
     Below Average 2 "cracks>1mm"
     Poor 3          "Spalling in reinforced concrete with main reinforcement
                      bars exposed and rusting"

   That matters more than it looks. A register that rated a 0.5 mm crack `2`
   instead of `1` would push work up the priority list ahead of structures that
   need it more, and it would do so consistently and invisibly.

   IT SUGGESTS. IT DOES NOT SET. Every function here returns a proposal with the
   rule that produced it, for a control to show beside the inspector's own
   choice. Nothing in this application writes a rating nobody selected: a rating
   is a statement an inspector made at a structure, and one the app inferred and
   stored silently would be the application making that statement in their name.
   --------------------------------------------------------------------------- */

import type { DefectQuantity } from './types.ts';

export interface RatingSuggestion {
  /** The condition rating Appendix D gives, on DoR's own 0–4 scale. */
  rating: number;
  /** What DoR calls it, for the control. */
  label: string;
  /** The rule, quotable, for printing beside the suggestion. */
  rule: string;
  /** Where the rule is written down. */
  source: string;
}

/** DoR's names for the five condition ratings, index = rating. */
export const CONDITION_LABELS: readonly string[] = [
  'Good',
  'Fair',
  'Below average',
  'Poor',
  'Serious',
];

/**
 * DoR's full definitions, §5.3. Printed under the picker and on the sheet.
 *
 * `N` is not in this array because it is not a point on the scale — it is the
 * refusal to place the element on it. See CONDITION_N below.
 */
export const CONDITION_DEFINITIONS: readonly string[] = [
  'Good condition with no significant defects.',
  'Fair condition. The element or component shows a few non-structural defects.',
  'Below average. The element or component shows local defects of structural significance but functions as designed.',
  'Poor condition. The element or component shows numerous defects of structural significance which may soon prevent it from functioning as designed.',
  'Serious condition with severe damage. The element or component is not functioning as designed.',
];

export const CONDITION_N = 'Not applicable, or element not accessible for inspection.';

/** DoR's extent definitions, index = band. */
export const EXTENT_DEFINITIONS: readonly string[] = [
  'No defect or insignificant length or area of the element affected.',
  'Minor, less than 5% of length or area of the element affected.',
  'Significant, 5% to 20% of length or area of the element affected.',
  'Major, highly significant, more than 20% of the element affected.',
  'Extensive, most or all of the element affected.',
];

/** DoR's urgency definitions, index = band. */
export const URGENCY_DEFINITIONS: readonly string[] = [
  'No time limit for action.',
  'Non-urgent, attention required within 2 years.',
  'Attention required within a year.',
  'Attention required within 6 months (generally before the monsoon or in the present fiscal year).',
  'Immediate attention required.',
];

/** The one scale in this application that is fixed rather than read off the sheet. */
export const DOR_BAND_MAX = 4;

const SOURCE = 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 2, Appendix D';

/* --------------------------- condition, from a measurement --------------------------- */

/**
 * What Appendix D rates a crack of this width.
 *
 * Concrete only, and deck main members and cross girders specifically — that is
 * the row the numbers are printed on. Applying them to a masonry pier or a
 * timber beam would be extending a published rule to elements it was not written
 * for, which is exactly the guesswork this project refuses elsewhere. The caller
 * decides whether the element qualifies; see `suggestConditionFor`.
 *
 * The boundaries are DoR's own and are not tidy: the page reads `< 0.3 mm`,
 * `> 0.3mm <1mm`, `cracks>1mm`, which leaves 0.3 and 1.0 exactly on no band at
 * all. They are assigned DOWNWARD — a crack measured at exactly 0.3 mm is Good,
 * at exactly 1 mm is Fair — because the alternative is to rate a structure worse
 * on a boundary value the guideline did not place, and a gauge read to one
 * decimal lands on those two numbers constantly.
 */
export function crackWidthRating(widthMm: number): RatingSuggestion | null {
  if (!Number.isFinite(widthMm) || widthMm < 0) return null;

  if (widthMm <= 0.3) {
    return band(0, 'Cracks in concrete < 0.3 mm');
  }
  if (widthMm <= 1) {
    return band(1, 'Concrete cracks > 0.3 mm < 1 mm');
  }
  return band(2, 'Concrete cracks > 1 mm');
}

function band(rating: number, rule: string): RatingSuggestion {
  return {
    rating,
    label: CONDITION_LABELS[rating] ?? String(rating),
    rule,
    source: SOURCE,
  };
}

/**
 * The condition Appendix D suggests for one defect on one element.
 *
 * Null wherever the guideline has no number, which is most of the time and is
 * the honest answer. Three cases have one:
 *
 *   · a crack width on a concrete deck element
 *   · exposed and rusting reinforcement, which Appendix D puts at Poor 3
 *     regardless of area — it is a description of a state, not a measurement, and
 *     a defect logged as REE with any area at all is in that state
 *   · nothing else
 *
 * `elementId` is checked rather than trusted: the numeric crack bands are
 * printed on the deck row, and a crack on an abutment is rated by the abutment's
 * own (qualitative) row. Returning the deck's numbers for it would be inventing
 * a rule for an element the guideline rates differently.
 */
const CONCRETE_DECK_ELEMENTS = new Set(['deck']);

export function suggestConditionFor(
  code: string,
  elementId: string | null,
  q: DefectQuantity | null,
): RatingSuggestion | null {
  if (!q || q.method === 'not-measured') return null;

  if (code === 'REE' && (q.areaM2 ?? q.count ?? 0) > 0) {
    return band(3, 'Spalling in reinforced concrete with main reinforcement bars exposed and rusting');
  }

  if (code === 'CRK' && q.widthMm !== undefined && elementId !== null) {
    if (!CONCRETE_DECK_ELEMENTS.has(elementId)) return null;
    return crackWidthRating(q.widthMm);
  }

  return null;
}

/* --------------------------- extent, from a proportion --------------------------- */

export interface ExtentSuggestion {
  band: number;
  /** The proportion that produced it, per cent. */
  percent: number;
  definition: string;
  source: string;
}

/**
 * The extent band for a measured proportion of an element.
 *
 * **It tops out at 3, and that is not an oversight.** Bands 0 to 3 are defined
 * as numbers — insignificant, under 5%, 5 to 20%, over 20% — and can be computed
 * from a measurement and a denominator. Band 4 is defined as *"extensive, most
 * or all of the element affected"*, which is a subset of band 3 that DoR
 * describes in words and never in a percentage. There is no threshold to
 * compute it against, so this does not compute one: 4 stays the inspector's own
 * judgement, and a screen offering it does so as a choice rather than as a
 * derived value.
 *
 * Zero is the only input that gives 0. A non-zero measurement is by definition
 * not "no defect", and rounding a small measured area down to *insignificant*
 * would discard the one thing the inspector went to the trouble of measuring.
 */
export function extentBandFor(percent: number): ExtentSuggestion | null {
  if (!Number.isFinite(percent) || percent < 0) return null;

  const b = percent === 0 ? 0 : percent < 5 ? 1 : percent <= 20 ? 2 : 3;

  return {
    band: b,
    percent,
    definition: EXTENT_DEFINITIONS[b] ?? '',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 2, §5.3',
  };
}
